import React, { useEffect, useState, useCallback } from "react";
import { Save, Send, RotateCcw, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { defaultContent } from "../../data/defaultContent";
import { Field, TextInput, TextArea, Section, UploadField } from "./EditorFields";

const EditPageTab = () => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [hasUnpublished, setHasUnpublished] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-content");
      setContent({ ...defaultContent, ...(data?.draft || {}) });
      setHasUnpublished(!!data?.has_unpublished);
    } catch (e) {
      setError("No se pudo cargar el contenido");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (path, value) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let target = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        if (target[k] === undefined || target[k] === null) target[k] = {};
        target = target[k];
      }
      target[parts[parts.length - 1]] = value;
      return next;
    });
    setHasUnpublished(true);
  };

  const updateArrayItem = (path, index, key, value) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const arr = path.split(".").reduce((acc, k) => acc[k], next);
      if (arr && arr[index]) arr[index][key] = value;
      return next;
    });
    setHasUnpublished(true);
  };

  const handleSaveDraft = async () => {
    setError("");
    setSaving(true);
    try {
      await api.put("/admin/site-content", { content });
      setSavedAt(new Date());
      setHasUnpublished(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar borrador");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm("¿Publicar los cambios? Los visitantes verán esta versión inmediatamente.")) return;
    setError("");
    setPublishing(true);
    try {
      // Save then publish
      await api.put("/admin/site-content", { content });
      await api.post("/admin/site-content/publish");
      setHasUnpublished(false);
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al publicar");
    } finally {
      setPublishing(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("¿Restaurar el borrador a los valores por defecto del sitio? Tendrás que publicar para que sea visible.")) return;
    try {
      const { data } = await api.post("/admin/site-content/reset-defaults");
      if (data?.draft) setContent({ ...defaultContent, ...data.draft });
      setHasUnpublished(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al restaurar");
    }
  };

  if (loading) return <div style={{ color: "#5F5A72" }}>Cargando contenido…</div>;

  const hero = content.hero || {};
  const levels = content.levels || { items: [] };
  const method = content.method || { items: [] };
  const platform = content.platform || { bullets: [] };
  const age = content.age || { bullets: [] };
  const testimonial = content.testimonial || {};
  const plans = content.plans || { regular: {}, intensive: {} };
  const benefits = content.benefits || { referral: {}, group: {} };
  const finalCta = content.finalCta || {};
  const contact = content.contact || {};
  const footer = content.footer || {};

  return (
    <div>
      {/* Sticky bar */}
      <div
        style={{
          position: "sticky",
          top: 76,
          background: "#F4F1EA",
          padding: "12px 0",
          zIndex: 10,
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <button onClick={handleSaveDraft} className="btn-dark" disabled={saving}>
          <Save size={15} /> {saving ? "Guardando…" : "Guardar borrador"}
        </button>
        <button onClick={handlePublish} className="btn-orange" disabled={publishing}>
          <Send size={15} /> {publishing ? "Publicando…" : "Publicar cambios"}
        </button>
        <button onClick={handleReset} className="btn-ghost" style={{ fontSize: 13 }}>
          <RotateCcw size={13} /> Restaurar por defecto
        </button>
        {hasUnpublished && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#FF4F1A", fontSize: 13, fontWeight: 600 }}>
            <AlertCircle size={14} /> Cambios sin publicar
          </span>
        )}
        {!hasUnpublished && savedAt && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#1A8B43", fontSize: 13, fontWeight: 600 }}>
            <CheckCircle2 size={14} /> Publicado
          </span>
        )}
        {error && <span style={{ color: "#E83E0A", fontSize: 13 }}>{error}</span>}
      </div>

      <h2 className="h-display" style={{ fontSize: 30, margin: "0 0 8px", color: "#1A1352" }}>
        Editar página
      </h2>
      <p style={{ color: "#5F5A72", fontSize: 14, marginBottom: 20 }}>
        Los cambios solo se guardan como borrador hasta que pulses <strong>"Publicar cambios"</strong>.
      </p>

      {/* HERO */}
      <Section title="Hero (sección principal)" defaultOpen>
        <Field label="Pill texto (sobre el título)">
          <TextInput value={hero.pillText} onChange={(v) => update("hero.pillText", v)} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Título línea 1">
            <TextInput value={hero.titleLine1} onChange={(v) => update("hero.titleLine1", v)} />
          </Field>
          <Field label="Título línea 2 - prefijo">
            <TextInput value={hero.titleLine2Prefix} onChange={(v) => update("hero.titleLine2Prefix", v)} />
          </Field>
          <Field label="Línea 2 - palabra destacada (gradient)">
            <TextInput value={hero.titleLine2Highlight} onChange={(v) => update("hero.titleLine2Highlight", v)} />
          </Field>
          <Field label="Línea 3 - palabra destacada (gradient)">
            <TextInput value={hero.titleLine3Highlight} onChange={(v) => update("hero.titleLine3Highlight", v)} />
          </Field>
          <Field label="Título línea 4">
            <TextInput value={hero.titleLine4} onChange={(v) => update("hero.titleLine4", v)} />
          </Field>
          <Field label="Texto del botón">
            <TextInput value={hero.ctaText} onChange={(v) => update("hero.ctaText", v)} />
          </Field>
        </div>
        <Field label="Subtítulo / párrafo">
          <TextArea value={hero.subtitle} onChange={(v) => update("hero.subtitle", v)} rows={3} />
        </Field>

        <Field label="Vídeo del Hero (sube tu archivo MP4)" hint="Se reproduce solo al pulsar Play, con audio.">
          <UploadField value={hero.videoUrl} onChange={(v) => update("hero.videoUrl", v)} kind="video" />
        </Field>
        <Field label="Imagen de portada del vídeo (thumbnail)" hint="JPG/PNG. Se muestra hasta que el usuario pulse Play.">
          <UploadField value={hero.videoThumbnail} onChange={(v) => update("hero.videoThumbnail", v)} kind="image" />
        </Field>

        <h4 style={{ margin: "18px 0 8px", color: "#1A1352", fontSize: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>Chips flotantes</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Chip 1 - Título"><TextInput value={hero.chip1Title} onChange={(v) => update("hero.chip1Title", v)} /></Field>
          <Field label="Chip 1 - Subtítulo"><TextInput value={hero.chip1Subtitle} onChange={(v) => update("hero.chip1Subtitle", v)} /></Field>
          <Field label="Chip 2 - Título"><TextInput value={hero.chip2Title} onChange={(v) => update("hero.chip2Title", v)} /></Field>
          <Field label="Chip 2 - Subtítulo"><TextInput value={hero.chip2Subtitle} onChange={(v) => update("hero.chip2Subtitle", v)} /></Field>
        </div>

        <h4 style={{ margin: "18px 0 8px", color: "#1A1352", fontSize: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>Estadísticas (3 columnas)</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <Field label={`Stat ${i} - Valor`}>
                <TextInput value={hero[`stat${i}Value`]} onChange={(v) => update(`hero.stat${i}Value`, v)} />
              </Field>
              <Field label={`Stat ${i} - Etiqueta`}>
                <TextInput value={hero[`stat${i}Label`]} onChange={(v) => update(`hero.stat${i}Label`, v)} />
              </Field>
            </div>
          ))}
        </div>
      </Section>

      {/* TICKER */}
      <Section title="Barra animada (ticker)">
        {(content.ticker || []).map((t, idx) => (
          <Field key={idx} label={`Frase ${idx + 1}`}>
            <TextInput
              value={t}
              onChange={(v) => {
                setContent((prev) => {
                  const next = JSON.parse(JSON.stringify(prev));
                  next.ticker[idx] = v;
                  return next;
                });
                setHasUnpublished(true);
              }}
            />
          </Field>
        ))}
      </Section>

      {/* LEVELS */}
      <Section title="Niveles de estudio (4 tarjetas)">
        <Field label="Kicker"><TextInput value={levels.kicker} onChange={(v) => update("levels.kicker", v)} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Título línea 1"><TextInput value={levels.titleLine1} onChange={(v) => update("levels.titleLine1", v)} /></Field>
          <Field label="Highlight"><TextInput value={levels.titleLine2Highlight} onChange={(v) => update("levels.titleLine2Highlight", v)} /></Field>
          <Field label="Sufijo"><TextInput value={levels.titleLine2Suffix} onChange={(v) => update("levels.titleLine2Suffix", v)} /></Field>
        </div>
        <Field label="Subtítulo"><TextArea value={levels.subtitle} onChange={(v) => update("levels.subtitle", v)} rows={2} /></Field>

        {(levels.items || []).map((it, i) => (
          <div key={i} style={{ background: "#FBF9F4", padding: 14, borderRadius: 12, marginTop: 12 }}>
            <h5 style={{ margin: "0 0 10px", color: "#1A1352" }}>Nivel {i + 1}</h5>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="N°"><TextInput value={it.n} onChange={(v) => updateArrayItem("levels.items", i, "n", v)} /></Field>
              <Field label="Código (A1, A2…)"><TextInput value={it.code} onChange={(v) => updateArrayItem("levels.items", i, "code", v)} /></Field>
              <Field label="Título"><TextInput value={it.title} onChange={(v) => updateArrayItem("levels.items", i, "title", v)} /></Field>
            </div>
            <Field label="Descripción"><TextArea value={it.desc} onChange={(v) => updateArrayItem("levels.items", i, "desc", v)} rows={2} /></Field>
            <UploadField label="Imagen" value={it.image} onChange={(v) => updateArrayItem("levels.items", i, "image", v)} kind="image" />
          </div>
        ))}
      </Section>

      {/* METHOD */}
      <Section title="Metodología (4 pilares)">
        <Field label="Kicker"><TextInput value={method.kicker} onChange={(v) => update("method.kicker", v)} /></Field>
        <Field label="Subtítulo"><TextArea value={method.subtitle} onChange={(v) => update("method.subtitle", v)} rows={2} /></Field>
        {(method.items || []).map((it, i) => (
          <div key={i} style={{ background: "#FBF9F4", padding: 14, borderRadius: 12, marginTop: 12 }}>
            <h5 style={{ margin: "0 0 10px", color: "#1A1352" }}>Pilar {i + 1}</h5>
            <Field label="Título"><TextInput value={it.title} onChange={(v) => updateArrayItem("method.items", i, "title", v)} /></Field>
            <Field label="Descripción"><TextArea value={it.desc} onChange={(v) => updateArrayItem("method.items", i, "desc", v)} rows={2} /></Field>
            <UploadField label="Imagen" value={it.image} onChange={(v) => updateArrayItem("method.items", i, "image", v)} kind="image" />
          </div>
        ))}
      </Section>

      {/* PLANS */}
      <Section title="Planes y precios">
        <Field label="Kicker"><TextInput value={plans.kicker} onChange={(v) => update("plans.kicker", v)} /></Field>
        <Field label="Inscripción única"><TextInput value={plans.inscriptionFee} onChange={(v) => update("plans.inscriptionFee", v)} /></Field>
        <Field label="Subtítulo"><TextArea value={plans.subtitle} onChange={(v) => update("plans.subtitle", v)} rows={2} /></Field>

        {["regular", "intensive"].map((key) => {
          const p = plans[key] || {};
          return (
            <div key={key} style={{ background: "#FBF9F4", padding: 14, borderRadius: 12, marginTop: 14 }}>
              <h5 style={{ margin: "0 0 10px", color: "#1A1352", textTransform: "capitalize" }}>Plan {key === "regular" ? "Regular" : "Intensivo"}</h5>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="Nombre"><TextInput value={p.name} onChange={(v) => update(`plans.${key}.name`, v)} /></Field>
                <Field label="Precio"><TextInput value={p.price} onChange={(v) => update(`plans.${key}.price`, v)} /></Field>
                <Field label="Sufijo (/mes)"><TextInput value={p.priceSuffix} onChange={(v) => update(`plans.${key}.priceSuffix`, v)} /></Field>
              </div>
              <Field label="Badge"><TextInput value={p.badge} onChange={(v) => update(`plans.${key}.badge`, v)} /></Field>
              <Field label="Descripción"><TextArea value={p.description} onChange={(v) => update(`plans.${key}.description`, v)} rows={2} /></Field>
              <Field label="Texto del botón CTA"><TextInput value={p.ctaText} onChange={(v) => update(`plans.${key}.ctaText`, v)} /></Field>
              <h6 style={{ margin: "12px 0 6px", color: "#1A1352", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Características</h6>
              {(p.features || []).map((f, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
                  <TextInput value={f.label} onChange={(v) => updateArrayItem(`plans.${key}.features`, i, "label", v)} placeholder="Etiqueta" />
                  <TextInput value={f.value} onChange={(v) => updateArrayItem(`plans.${key}.features`, i, "value", v)} placeholder="Valor" />
                </div>
              ))}
            </div>
          );
        })}
      </Section>

      {/* BENEFITS */}
      <Section title="Beneficios (2 tarjetas)">
        <Field label="Kicker"><TextInput value={benefits.kicker} onChange={(v) => update("benefits.kicker", v)} /></Field>
        <div style={{ background: "#FBF9F4", padding: 14, borderRadius: 12, marginTop: 12 }}>
          <h5 style={{ margin: "0 0 10px", color: "#1A1352" }}>Beneficio por referidos</h5>
          <Field label="Kicker"><TextInput value={benefits.referral?.kicker} onChange={(v) => update("benefits.referral.kicker", v)} /></Field>
          <Field label="Título"><TextInput value={benefits.referral?.title} onChange={(v) => update("benefits.referral.title", v)} /></Field>
          <Field label="Descripción"><TextArea value={benefits.referral?.desc} onChange={(v) => update("benefits.referral.desc", v)} rows={2} /></Field>
          <UploadField label="Imagen" value={benefits.referral?.image} onChange={(v) => update("benefits.referral.image", v)} kind="image" />
        </div>
        <div style={{ background: "#FBF9F4", padding: 14, borderRadius: 12, marginTop: 12 }}>
          <h5 style={{ margin: "0 0 10px", color: "#1A1352" }}>Inscripción en grupo</h5>
          <Field label="Kicker"><TextInput value={benefits.group?.kicker} onChange={(v) => update("benefits.group.kicker", v)} /></Field>
          <Field label="Título"><TextInput value={benefits.group?.title} onChange={(v) => update("benefits.group.title", v)} /></Field>
          <Field label="Descripción"><TextArea value={benefits.group?.desc} onChange={(v) => update("benefits.group.desc", v)} rows={2} /></Field>
          <Field label="Texto del botón"><TextInput value={benefits.group?.ctaText} onChange={(v) => update("benefits.group.ctaText", v)} /></Field>
        </div>
      </Section>

      {/* PLATFORM / AGE / TESTIMONIAL */}
      <Section title="Plataforma">
        <Field label="Kicker"><TextInput value={platform.kicker} onChange={(v) => update("platform.kicker", v)} /></Field>
        <Field label="Subtítulo"><TextArea value={platform.subtitle} onChange={(v) => update("platform.subtitle", v)} rows={3} /></Field>
        {(platform.bullets || []).map((b, i) => (
          <Field key={i} label={`Bullet ${i + 1}`}>
            <TextInput value={b} onChange={(v) => {
              setContent((p) => {
                const n = JSON.parse(JSON.stringify(p));
                n.platform.bullets[i] = v;
                return n;
              });
              setHasUnpublished(true);
            }} />
          </Field>
        ))}
      </Section>

      <Section title="Sección 12+ (jóvenes y adultos)">
        <Field label="Kicker"><TextInput value={age.kicker} onChange={(v) => update("age.kicker", v)} /></Field>
        <Field label="Subtítulo"><TextArea value={age.subtitle} onChange={(v) => update("age.subtitle", v)} rows={3} /></Field>
        <Field label="Texto del chip 12+"><TextArea value={age.chipText} onChange={(v) => update("age.chipText", v)} rows={2} /></Field>
        {(age.bullets || []).map((b, i) => (
          <Field key={i} label={`Bullet ${i + 1}`}>
            <TextInput value={b} onChange={(v) => {
              setContent((p) => {
                const n = JSON.parse(JSON.stringify(p));
                n.age.bullets[i] = v;
                return n;
              });
              setHasUnpublished(true);
            }} />
          </Field>
        ))}
      </Section>

      <Section title="Testimonio / Validez internacional">
        <Field label="Kicker"><TextInput value={testimonial.kicker} onChange={(v) => update("testimonial.kicker", v)} /></Field>
        <Field label="Frase principal (entre comillas)"><TextArea value={testimonial.quote} onChange={(v) => update("testimonial.quote", v)} rows={3} /></Field>
        <Field label="Subtítulo"><TextArea value={testimonial.subtitle} onChange={(v) => update("testimonial.subtitle", v)} rows={3} /></Field>
        <Field label="Chip - Título"><TextInput value={testimonial.chipTitle} onChange={(v) => update("testimonial.chipTitle", v)} /></Field>
        <Field label="Chip - Subtítulo"><TextInput value={testimonial.chipSubtitle} onChange={(v) => update("testimonial.chipSubtitle", v)} /></Field>
      </Section>

      <Section title="CTA Final (banner naranja)">
        <Field label="Kicker"><TextInput value={finalCta.kicker} onChange={(v) => update("finalCta.kicker", v)} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Línea 1"><TextInput value={finalCta.titleLine1} onChange={(v) => update("finalCta.titleLine1", v)} /></Field>
          <Field label="Línea 2"><TextInput value={finalCta.titleLine2} onChange={(v) => update("finalCta.titleLine2", v)} /></Field>
          <Field label="Línea 3"><TextInput value={finalCta.titleLine3} onChange={(v) => update("finalCta.titleLine3", v)} /></Field>
        </div>
        <Field label="Subtítulo"><TextArea value={finalCta.subtitle} onChange={(v) => update("finalCta.subtitle", v)} rows={2} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Texto botón 1"><TextInput value={finalCta.primaryCtaText} onChange={(v) => update("finalCta.primaryCtaText", v)} /></Field>
          <Field label="Texto botón 2"><TextInput value={finalCta.secondaryCtaText} onChange={(v) => update("finalCta.secondaryCtaText", v)} /></Field>
        </div>
      </Section>

      <Section title="Contacto">
        <Field label="Kicker"><TextInput value={contact.kicker} onChange={(v) => update("contact.kicker", v)} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Línea 1"><TextInput value={contact.titleLine1} onChange={(v) => update("contact.titleLine1", v)} /></Field>
          <Field label="Línea 2"><TextInput value={contact.titleLine2} onChange={(v) => update("contact.titleLine2", v)} /></Field>
          <Field label="Línea 3"><TextInput value={contact.titleLine3} onChange={(v) => update("contact.titleLine3", v)} /></Field>
        </div>
        <Field label="Subtítulo"><TextArea value={contact.subtitle} onChange={(v) => update("contact.subtitle", v)} rows={3} /></Field>
        <Field label="Mensaje de éxito - Título"><TextInput value={contact.successTitle} onChange={(v) => update("contact.successTitle", v)} /></Field>
        <Field label="Mensaje de éxito - Subtítulo"><TextInput value={contact.successSubtitle} onChange={(v) => update("contact.successSubtitle", v)} /></Field>
      </Section>

      <Section title="Footer">
        <Field label="Texto descriptivo"><TextArea value={footer.tagline} onChange={(v) => update("footer.tagline", v)} rows={3} /></Field>
        <Field label="Texto del pie"><TextInput value={footer.rightsText} onChange={(v) => update("footer.rightsText", v)} /></Field>
      </Section>

      {/* Bottom actions */}
      <div style={{ display: "flex", gap: 10, marginTop: 28 }}>
        <button onClick={handleSaveDraft} className="btn-dark" disabled={saving}>
          <Save size={15} /> {saving ? "Guardando…" : "Guardar borrador"}
        </button>
        <button onClick={handlePublish} className="btn-orange" disabled={publishing}>
          <Send size={15} /> {publishing ? "Publicando…" : "Publicar cambios"}
        </button>
      </div>
    </div>
  );
};

export default EditPageTab;
